import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ReviewStep = ({ onBack, onPlaceOrder, deliveryData, paymentData }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Mock cart items
  const cartItems = [
    {
      id: 1,
      name: 'Fresh Organic Bananas',
      image: 'https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=400',
      price: 2.99,
      quantity: 3,
      weight: '2 lbs'
    },
    {
      id: 2,
      name: 'Whole Milk - 1 Gallon',
      image: 'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=400',
      price: 3.49,
      quantity: 1,
      weight: '1 gallon'
    },
    {
      id: 3,
      name: 'Free Range Eggs - Dozen',
      image: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=400',
      price: 4.99,
      quantity: 2,
      weight: '24 eggs'
    }
  ];

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryFee = 2.99;
  const tax = subtotal * 0.08;
  const discount = paymentData.promoCode ? 5.00 : 0;
  const total = subtotal + deliveryFee + tax - discount;

  const handlePlaceOrder = async () => {
    if (!agreedToTerms) {
      alert('Please agree to the terms and conditions');
      return;
    }

    setIsProcessing(true);
    
    // Simulate order processing
    setTimeout(() => {
      setIsProcessing(false);
      onPlaceOrder();
    }, 3000);
  };

  const getAddressDisplay = () => {
    if (deliveryData.isNewAddress) {
      const addr = deliveryData.selectedAddress;
      return `${addr.fullName}, ${addr.street}, ${addr.city}, ${addr.state} ${addr.zipCode}`;
    } else {
      // Mock saved address display
      return "John Smith, 123 Main Street, New York, NY 10001";
    }
  };

  const getPaymentDisplay = () => {
    if (paymentData.isNewCard) {
      return `•••• •••• •••• ${paymentData.selectedMethod.cardNumber.slice(-4)}`;
    } else {
      return "Visa •••• 4242";
    }
  };

  const getTimeSlotDisplay = () => {
    const timeSlots = [
      { id: 1, time: '9:00 AM - 11:00 AM' },
      { id: 2, time: '11:00 AM - 1:00 PM' },
      { id: 3, time: '1:00 PM - 3:00 PM' },
      { id: 4, time: '3:00 PM - 5:00 PM' },
      { id: 5, time: '5:00 PM - 7:00 PM' },
      { id: 6, time: '7:00 PM - 9:00 PM' }
    ];
    
    const selectedSlot = timeSlots.find(slot => slot.id === deliveryData.timeSlot);
    return selectedSlot ? selectedSlot.time : 'Not selected';
  };

  return (
    <div className="space-y-8">
      {/* Order Items */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Order Items ({cartItems.length})
        </h3>
        
        <div className="space-y-4">
          {cartItems.map((item) => (
            <div key={item.id} className="flex items-center space-x-4 p-4 bg-background rounded-card">
              <Image
                src={item.image}
                alt={item.name}
                className="w-16 h-16 object-cover rounded-input flex-shrink-0"
              />
              <div className="flex-1">
                <h4 className="font-body-semibold text-text-primary">{item.name}</h4>
                <p className="text-text-secondary text-sm">{item.weight}</p>
                <p className="font-data font-data-medium text-primary">
                  ${item.price.toFixed(2)} × {item.quantity}
                </p>
              </div>
              <div className="text-right">
                <p className="font-data font-data-medium text-text-primary">
                  ${(item.price * item.quantity).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delivery Information */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Delivery Information
        </h3>
        
        <div className="p-4 bg-background rounded-card space-y-3">
          <div className="flex items-start space-x-3">
            <Icon name="MapPin" size={20} className="text-text-secondary mt-0.5" />
            <div>
              <p className="font-body-semibold text-text-primary">Delivery Address</p>
              <p className="text-text-secondary">{getAddressDisplay()}</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <Icon name="Clock" size={20} className="text-text-secondary mt-0.5" />
            <div>
              <p className="font-body-semibold text-text-primary">Delivery Time</p>
              <p className="text-text-secondary">{getTimeSlotDisplay()}</p>
            </div>
          </div>
          
          {deliveryData.instructions && (
            <div className="flex items-start space-x-3">
              <Icon name="MessageSquare" size={20} className="text-text-secondary mt-0.5" />
              <div>
                <p className="font-body-semibold text-text-primary">Special Instructions</p>
                <p className="text-text-secondary">{deliveryData.instructions}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Information */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Payment Information
        </h3>
        
        <div className="p-4 bg-background rounded-card space-y-3">
          <div className="flex items-center space-x-3">
            <Icon name="CreditCard" size={20} className="text-text-secondary" />
            <div>
              <p className="font-body-semibold text-text-primary">Payment Method</p>
              <p className="text-text-secondary">{getPaymentDisplay()}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Icon name="MapPin" size={20} className="text-text-secondary" />
            <div>
              <p className="font-body-semibold text-text-primary">Billing Address</p>
              <p className="text-text-secondary">
                {paymentData.billingAddress === 'same' ?'Same as delivery address' :'Different billing address'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div>
        <h3 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
          Order Summary
        </h3>
        
        <div className="p-4 bg-background rounded-card space-y-3">
          <div className="flex justify-between">
            <span className="text-text-secondary">Subtotal</span>
            <span className="font-data font-data-medium text-text-primary">
              ${subtotal.toFixed(2)}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-text-secondary">Delivery Fee</span>
            <span className="font-data font-data-medium text-text-primary">
              ${deliveryFee.toFixed(2)}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-text-secondary">Tax</span>
            <span className="font-data font-data-medium text-text-primary">
              ${tax.toFixed(2)}
            </span>
          </div>
          
          {discount > 0 && (
            <div className="flex justify-between">
              <span className="text-success">Discount ({paymentData.promoCode})</span>
              <span className="font-data font-data-medium text-success">
                -${discount.toFixed(2)}
              </span>
            </div>
          )}
          
          <div className="border-t border-border pt-3">
            <div className="flex justify-between">
              <span className="font-heading font-heading-semibold text-lg text-text-primary">
                Total
              </span>
              <span className="font-data font-data-medium text-xl text-primary">
                ${total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          id="terms"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
          className="mt-1"
        />
        <label htmlFor="terms" className="text-text-secondary text-sm">
          I agree to the{' '}
          <a href="#" className="text-primary hover:underline">
            Terms and Conditions
          </a>{' '}
          and{' '}
          <a href="#" className="text-primary hover:underline">
            Privacy Policy
          </a>
        </label>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          iconName="ArrowLeft"
          iconPosition="left"
          disabled={isProcessing}
        >
          Back to Payment
        </Button>
        
        <Button
          variant="primary"
          onClick={handlePlaceOrder}
          disabled={!agreedToTerms || isProcessing}
          loading={isProcessing}
          className="px-8"
        >
          {isProcessing ? 'Processing...' : 'Place Order'}
        </Button>
      </div>

      {/* Security Notice */}
      <div className="flex items-center justify-center space-x-4 pt-4 text-text-secondary text-sm">
        <div className="flex items-center space-x-1">
          <Icon name="Shield" size={16} />
          <span>SSL Secured</span>
        </div>
        <div className="flex items-center space-x-1">
          <Icon name="Lock" size={16} />
          <span>256-bit Encryption</span>
        </div>
        <div className="flex items-center space-x-1">
          <Icon name="CheckCircle" size={16} />
          <span>PCI Compliant</span>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;